import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { RecordPicker } from '../../components/RecordPicker.js';
import { personLabel } from '../../components/ApprovalLevels.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';

interface AuditRow {
  readonly auditId: string;
  readonly eventType: string;
  readonly entityType: string;
  readonly entityCode: string | null;
  readonly action: string;
  readonly actorName: string | null;
  readonly actorCode: string | null;
  readonly createdAt: string;
}

const PAGE = 25;

/**
 * M43 §27 Audit Dashboard (§25 Audit Events). Read-only view over the T566
 * append-only tenant audit ledger — every tenant / subscription / branding /
 * domain / feature / usage / security / backup / restore / admin action.
 * Filterable by event type, entity type and actor. Viewing needs AUDIT_VIEW.
 */
export function AuditDashboard(): ReactNode {
  const { token } = useAuth();
  // Instants follow the tenant's DISPLAY_DATE_FORMAT, not the reader's language picker.
  const { at } = useBusinessDay();
  const [rows, setRows] = useState<AuditRow[]>([]);
  const [total, setTotal] = useState(0);
  const [eventType, setEventType] = useState('');
  const [entityType, setEntityType] = useState('');
  const [actorId, setActorId] = useState('');
  const [page, setPage] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (eventType.trim()) params.set('eventType', eventType.trim());
    if (entityType.trim()) params.set('entityType', entityType.trim());
    if (actorId.trim()) params.set('actorId', actorId.trim());
    params.set('pageSize', String(PAGE));
    params.set('page', String(page + 1));
    apiRequest<{ data: AuditRow[]; total: number }>(`/v1/saas/audit?${params.toString()}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setTotal(r.total); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, eventType, entityType, actorId, page]);

  return (
    <main aria-label="audit-dashboard">
      <h1>Audit Dashboard</h1>

      <section aria-label="audit-filters">
        <label>Event type<input aria-label="filter-event-type" value={eventType} onChange={(e) => { setEventType(e.target.value); setPage(0); }} /></label>
        <label>Entity type<input aria-label="filter-entity-type" value={entityType} onChange={(e) => { setEntityType(e.target.value); setPage(0); }} /></label>
        <RecordPicker<{ userId: string; displayName?: string; employeeCode?: string }>
          endpoint="/v1/users?limit=200"
          idField="userId"
          label="Actor"
          testId="filter-actor-id"
          value={actorId}
          optionLabel={(u) =>
            [u.employeeCode, u.displayName].filter(Boolean).join(' \u2014 ') || u.userId
          }
          onPick={(id) => { setActorId(id); setPage(0); }}
          emptyText="No user on file"
        />
      </section>

      {error && <p role="alert" aria-label="audit-error">{error}</p>}
      {loading ? <p>Loading…</p> : (
        <>
          <p aria-label="audit-total">{total} event(s).</p>
          <table aria-label="audit-table">
            <thead><tr><th scope="col">Event</th><th scope="col">Entity</th><th scope="col">Code</th><th scope="col">Action</th><th scope="col">Actor</th><th scope="col">At</th></tr></thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan={6}>No audit events.</td></tr>
              ) : rows.map((a) => (
                <tr key={a.auditId} data-audit={a.auditId}>
                  <td aria-label={`audit-event-${a.auditId}`}>{a.eventType}</td>
                  <td>{a.entityType}</td>
                  <td>{a.entityCode ?? '—'}</td>
                  <td aria-label={`audit-action-${a.auditId}`}>{a.action}</td>
                  <td>{personLabel(a.actorName, a.actorCode) ?? '—'}</td>
                  <td>{at(a.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <nav aria-label="audit-pagination">
            <button type="button" aria-label="prev-page" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>Previous</button>
            <span aria-label="page-indicator">Page {page + 1} of {Math.max(1, Math.ceil(total / PAGE))}</span>
            <button type="button" aria-label="next-page" disabled={(page + 1) * PAGE >= total} onClick={() => setPage((p) => p + 1)}>Next</button>
          </nav>
        </>
      )}
    </main>
  );
}
